import i18n from "i18n";
import * as Yup from "yup";

import { ProductType } from "constants/hst-constants";
import type { SelectedProduct } from "../../../StudentLicense/subpages/CreateInviteForm/components/AddProduct/AddProduct";

export interface NewLinkInviteFormFields {
  // Step 1: Product and education selection
  selectedEducation: string;
  selectedProducts: SelectedProduct[];
  licenseExpirationDate: Date; // License expiration cannot be edited just informative
  maxUsageLimit: number; // The maximum number of times the invite link can be used, obligatory and must be greater than 0;

  // Step 2: Group selection
  groupAssignmentMethod: string;
  groupId?: number;
  newGroupName?: string;
}

export const buildNewLinkInviteFormSchema = (): Yup.SchemaOf<NewLinkInviteFormFields> =>
  Yup.object({
    selectedEducation: Yup.string().required(
      i18n.t(
        "modal:createLinkInviteModal.steps.productSelection.fields.selectedEducation.errors.required",
      ),
    ),
    selectedProducts: Yup.array()
      .of(
        Yup.object({
          id: Yup.number().required(),
          name: Yup.string().required(),
          type: Yup.string().optional(),
          initialSkillLevel: Yup.number().when("type", {
            is: ProductType.BUSINESS,
            // biome-ignore lint/suspicious/noThenProperty: This "then" keyword is valid "Yup" syntax, cannot be avoided
            then: (schema) =>
              schema.required(
                i18n.t(
                  "modal:createLinkInviteModal.steps.productSelection.fields.initialSkillLevel.errors.required",
                ),
              ),
            otherwise: (schema) => schema.optional(),
          }),
        }),
      )
      .default([])
      .min(
        1,
        i18n.t(
          "modal:createLinkInviteModal.steps.productSelection.fields.selectedProducts.errors.min",
        ),
      )
      .required(
        i18n.t(
          "modal:createLinkInviteModal.steps.productSelection.fields.selectedProducts.errors.required",
        ),
      ),
    licenseExpirationDate: Yup.date()
      .required(
        i18n.t(
          "modal:createLinkInviteModal.steps.productSelection.fields.licenseExpirationDate.errors.required",
        ),
      )
      .min(
        new Date(),
        i18n.t(
          "modal:createLinkInviteModal.steps.productSelection.fields.licenseExpirationDate.errors.min",
        ),
      ),
    maxUsageLimit: Yup.number()
      .integer()
      .min(
        1,
        i18n.t(
          "modal:createLinkInviteModal.steps.productSelection.fields.maxUsageLimit.errors.min",
        ),
      )
      .required(
        i18n.t(
          "modal:createLinkInviteModal.steps.productSelection.fields.maxUsageLimit.errors.required",
        ),
      ),
    groupAssignmentMethod: Yup.string().oneOf(["existing", "new", "none"]).required(),
    groupId: Yup.number().when("groupAssignmentMethod", {
      is: "existing",
      // biome-ignore lint/suspicious/noThenProperty: This "then" keyword is valid "Yup" syntax, cannot be avoided
      then: (schema) =>
        schema.required(
          i18n.t("modal:createLinkInviteModal.steps.assignGroup.fields.groupId.errors.required"),
        ),
      otherwise: (schema) => schema.optional(),
    }),
    newGroupName: Yup.string().when("groupAssignmentMethod", {
      is: "new",
      // biome-ignore lint/suspicious/noThenProperty: This "then" keyword is valid "Yup" syntax, cannot be avoided
      then: (schema) =>
        schema.required(
          i18n.t(
            "modal:createLinkInviteModal.steps.assignGroup.fields.newGroupName.errors.required",
          ),
        ),
      otherwise: (schema) => schema.optional(),
    }),
  }).required();
